/* global FB */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { notify, confirmAsync } from '../utils/notify';
import './WhatsAppNumbers.css';

function WhatsAppNumbers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualForm, setManualForm] = useState({
    phoneNumberId: '',
    businessAccountId: '',
    accessToken: '',
    phoneNumber: ''
  });
  const [lookupLoading, setLookupLoading] = useState(false);
  const [autoLookupLoading, setAutoLookupLoading] = useState(false);
  const [embeddedSignupData, setEmbeddedSignupData] = useState(null);
  const [showAgentSelectionModal, setShowAgentSelectionModal] = useState(false);
  const [phonesForAgentSelection, setPhonesForAgentSelection] = useState([]);
  const [agentSelections, setAgentSelections] = useState({});
  const [savingNumbers, setSavingNumbers] = useState(false);
  
  // PIN Verification state (legacy - now using auto-registration)
  // These states are kept for potential future use but PIN modal is removed
  const [showPinModal, setShowPinModal] = useState(false); // No longer used - auto-registration handles PIN
  const [pendingPhone, setPendingPhone] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [pinCode, setPinCode] = useState(''); // Deprecated: PIN now auto-generated server-side
  // eslint-disable-next-line no-unused-vars
  const [pinCodeMethod, setPinCodeMethod] = useState('SMS'); // Deprecated: not used with auto-registration
  // eslint-disable-next-line no-unused-vars
  const [pinRequesting, setPinRequesting] = useState(false); // Deprecated
  const [pinVerifying, setPinVerifying] = useState(false); // Still used for loading state during auto-register
  // eslint-disable-next-line no-unused-vars
  const [pinCodeSent, setPinCodeSent] = useState(false); // Deprecated

  // Mobile OAuth Code Modal state (when we receive code but no WABA ID)
  const [showMobileCodeModal, setShowMobileCodeModal] = useState(false);
  const [mobileOAuthCode, setMobileOAuthCode] = useState(null);
  const [mobilePhoneInput, setMobilePhoneInput] = useState('');
  const [mobilePhoneLookupLoading, setMobilePhoneLookupLoading] = useState(false);
  const [mobilePhoneLookupResult, setMobilePhoneLookupResult] = useState(null);

  // Deregistration PIN Modal state
  const [showDeregisterPinModal, setShowDeregisterPinModal] = useState(false);
  const [deregisterPinCode, setDeregisterPinCode] = useState('');
  const [deregisterNumberId, setDeregisterNumberId] = useState(null);
  const [deregisterLoading, setDeregisterLoading] = useState(false);
  const [showMobileConfirmation, setShowMobileConfirmation] = useState(false);

  // Business Profile Settings state
  const [expandedProfileId, setExpandedProfileId] = useState(null);
  const [profileForms, setProfileForms] = useState({});
  const [profileSaving, setProfileSaving] = useState({});
  const [profileSyncing, setProfileSyncing] = useState({});
  const [photoUploading, setPhotoUploading] = useState({});
  const fileInputRefs = useRef({});

  // Mobile detection - browsers on mobile behave differently with OAuth popups
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Fetch numbers function wrapped in useCallback
  const fetchNumbers = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/whatsapp/numbers');
      setNumbers(response.data);
    } catch (error) {
      console.error('Failed to fetch WhatsApp numbers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper function to proceed to agent selection
  const proceedToAgentSelection = useCallback((phoneNumberId, wabaId, originalData) => {
    const phoneData = [{
      phone_number_id: phoneNumberId,
      waba_id: wabaId,
      display_phone_number: originalData.display_phone_number || 'New WhatsApp Number',
      verified_name: originalData.verified_name || ''
    }];
    
    const initialSelections = {
      [phoneNumberId]: 'customer_care'
    };
    
    setPhonesForAgentSelection(phoneData);
    setAgentSelections(initialSelections);
    setShowAgentSelectionModal(true);
  }, []);

  // Auto-register phone with system-generated PIN
  const autoRegisterPhone = useCallback(async (phoneNumberId, wabaId, originalData) => {
    console.log('%c🔐 Auto-registering phone with generated PIN...', 'color: blue; font-size: 14px; font-weight: bold;');
    
    try {
      // Note: Router is at /api/v1/whatsapp, NOT /api/v1/whatsapp-numbers
      const autoRegResponse = await apiClient.post('/api/v1/whatsapp/phone-verification/auto-register', {
        phone_number_id: phoneNumberId,
        waba_id: wabaId,
        display_phone_number: originalData.display_phone_number,
        verified_name: originalData.verified_name
      });
      
      if (autoRegResponse.data.success) {
        console.log('%c✅ Phone auto-registered successfully!', 'color: green; font-size: 14px; font-weight: bold;');
        
        // Proceed directly to agent selection
        proceedToAgentSelection(phoneNumberId, wabaId, {
          display_phone_number: autoRegResponse.data.display_phone_number || originalData.display_phone_number,
          verified_name: autoRegResponse.data.verified_name || originalData.verified_name
        });
        return true;
      }
    } catch (autoRegError) {
      console.error('Auto-registration failed:', autoRegError);
      const errorMsg = autoRegError.response?.data?.detail || 'Auto-registration failed';
      console.log('%c⚠️ Auto-register error: ' + errorMsg, 'color: orange;');
    }
    return false;
  }, [proceedToAgentSelection]);

  // Check phone verification status after Embedded Signup
  const checkPhoneVerificationStatus = useCallback(async (phoneNumberId, wabaId, originalData) => {
    console.group('🔍 Checking phone verification status...');
    
    // Clear pending signup from localStorage since we're processing it now
    localStorage.removeItem('whatsapp_pending_signup');
    
    try {
      const response = await apiClient.post('/api/v1/whatsapp/phone-verification/check-status', null, {
        params: {
          phone_number_id: phoneNumberId,
          waba_id: wabaId
        }
      });
      
      const phoneStatus = response.data.status;
      const codeVerificationStatus = response.data.code_verification_status;
      
      console.log('Phone Status:', phoneStatus);
      console.groupEnd();
      
      if (phoneStatus === 'PENDING') {
        console.log('%c⚠️ Phone is PENDING - auto-registering with generated PIN...', 'color: orange; font-size: 14px; font-weight: bold;');
        
        // AUTO-REGISTER with generated PIN instead of showing PIN modal
        const success = await autoRegisterPhone(phoneNumberId, wabaId, {
          display_phone_number: originalData.display_phone_number || response.data.display_phone_number,
          verified_name: originalData.verified_name || response.data.verified_name
        });
        
        if (!success) {
          // Fallback: proceed to agent selection anyway (phone might work)
          console.log('%c⚠️ Auto-register failed, proceeding to agent selection anyway...', 'color: orange;');
          proceedToAgentSelection(phoneNumberId, wabaId, originalData);
        }
        
      } else if (phoneStatus === 'CONNECTED' || codeVerificationStatus === 'VERIFIED') {
        console.log('%c✅ Phone is CONNECTED/VERIFIED!', 'color: green; font-size: 14px; font-weight: bold;');
        proceedToAgentSelection(phoneNumberId, wabaId, originalData);
        
      } else {
        // Unknown status - try auto-register anyway
        console.log(`%c⚠️ Unknown status: ${phoneStatus} - attempting auto-register...`, 'color: orange;');
        
        const success = await autoRegisterPhone(phoneNumberId, wabaId, originalData);
        
        if (!success) {
          // Fallback: proceed to agent selection
          proceedToAgentSelection(phoneNumberId, wabaId, originalData);
        }
      }
    } catch (error) {
      console.error('Failed to check verification status:', error);
      console.groupEnd();
      
      // On any error, try auto-register then proceed to agent selection
      const success = await autoRegisterPhone(phoneNumberId, wabaId, originalData);
      
      if (!success) {
        proceedToAgentSelection(phoneNumberId, wabaId, originalData);
      }
    }
  }, [autoRegisterPhone, proceedToAgentSelection]);

  // Process pending OAuth data - centralized function for both mobile and desktop
  const processPendingOAuthData = useCallback(() => {
    //console.log('📱 Checking for pending OAuth data...', isMobile ? '(Mobile)' : '(Desktop)');
    
    // Check localStorage for pending signup data
    const pendingSignup = localStorage.getItem('whatsapp_pending_signup');
    if (pendingSignup) {
      try {
        const data = JSON.parse(pendingSignup);
        //console.log('📱 Found pending signup data:', data);
        
        // Check if data is not expired (max 10 minutes)
        const timestamp = new Date(data.timestamp).getTime();
        const now = Date.now();
        const maxAge = 10 * 60 * 1000; // 10 minutes
        
        if (now - timestamp > maxAge) {
          //console.log('📱 Pending signup data expired, removing...');
          localStorage.removeItem('whatsapp_pending_signup');
          return false;
        }
        
        if (data.phone_number_id && data.waba_id) {
          localStorage.removeItem('whatsapp_pending_signup');
          checkPhoneVerificationStatus(data.phone_number_id, data.waba_id, data);
          return true;
        }
      } catch (e) {
        console.error('Failed to parse pending signup data:', e);
        localStorage.removeItem('whatsapp_pending_signup');
      }
    }
    
    // Check URL params (for redirect-based OAuth on mobile)
    const urlParams = new URLSearchParams(window.location.search);
    const oauthComplete = urlParams.get('oauth_complete');
    const phonesEncoded = urlParams.get('phones');
    const wabaId = urlParams.get('waba_id');
    const phoneNumberId = urlParams.get('phone_number_id');
    
    // Direct WABA params from callback
    if (wabaId && phoneNumberId) {
      //console.log('📱 Found WABA params in URL:', { wabaId, phoneNumberId });
      window.history.replaceState({}, document.title, window.location.pathname);
      
      checkPhoneVerificationStatus(phoneNumberId, wabaId, {
        display_phone_number: urlParams.get('display_phone_number') || '',
        verified_name: urlParams.get('verified_name') || ''
      });
      return true;
    }
    
    // Encoded phones data
    if (oauthComplete === 'true' && phonesEncoded) {
      try {
        const phonesJson = atob(phonesEncoded);
        const phones = JSON.parse(phonesJson);
        
        //console.log('📱 Found OAuth params in URL:', phones);
        window.history.replaceState({}, document.title, window.location.pathname);
        
        const initialSelections = {};
        phones.forEach((phone, index) => {
          initialSelections[phone.phone_number_id] = index === 0 ? 'customer_care' : 'tutor';
        });
        
        setPhonesForAgentSelection(phones);
        setAgentSelections(initialSelections);
        setShowAgentSelectionModal(true);
        return true;
      } catch (e) {
        console.error('Failed to parse phones from URL:', e);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    
    return false;
  }, [checkPhoneVerificationStatus, isMobile]);

  useEffect(() => {
    fetchNumbers();
    
    // Process any pending OAuth data immediately (handles page reload on mobile)
    processPendingOAuthData();
    
    // Listen for Embedded Signup events from Meta OAuth popup
    const sessionInfoListener = (event) => {
      // Validate origin
      if (event.origin !== "https://www.facebook.com" && event.origin !== "https://web.facebook.com") {
        return;
      }
      
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.group('🎯 Meta Embedded Signup Event Received');
          //console.log('Event Type:', data.type);
          //console.log('Event Name:', data.event);
          //console.log('Full Event Data:', data);
          //console.log('Timestamp:', new Date().toISOString());
          console.groupEnd();
          
          // User finishes the Embedded Signup flow
          if (data.event === 'FINISH') {
            const { phone_number_id, waba_id } = data.data;
            
            console.group('✅ EMBEDDED SIGNUP FINISHED - WABA CAPTURED');
            //console.log('🆔 WABA ID (Business Account ID):', waba_id);
            //console.log('📱 Phone Number ID:', phone_number_id);
            //console.log('📊 Full Data Object:', data.data);
            console.groupEnd();
            
            // Store the data including FB auth response
            const finishedData = {
              phone_number_id,
              waba_id,
              status: 'finished',
              fbAuthResponse: embeddedSignupData?.fbAuthResponse || null,
              display_phone_number: data.data.display_phone_number || '',
              verified_name: data.data.verified_name || '',
              timestamp: new Date().toISOString()
            };
            
            setEmbeddedSignupData(finishedData);
            
            // Save to localStorage as backup for mobile (in case page gets suspended)
            localStorage.setItem('whatsapp_pending_signup', JSON.stringify(finishedData));
            //console.log('📱 Saved pending signup data to localStorage for mobile fallback');
            
            // Log to browser console for easy verification
            //console.log('%c🎉 WABA ID Successfully Captured!', 'color: green; font-size: 16px; font-weight: bold;');
            //console.log('%cWABA ID: ' + waba_id, 'color: #4caf50; font-size: 14px; font-weight: bold;');
            //console.log('%cPhone Number ID: ' + phone_number_id, 'color: #2196f3; font-size: 14px; font-weight: bold;');
            
            // Check phone status - if PENDING, show PIN modal
            checkPhoneVerificationStatus(phone_number_id, waba_id, data.data);
          }
          // User cancels the Embedded Signup flow
          else if (data.event === 'CANCEL') {
            const { current_step } = data.data;
            //console.group('❌ EMBEDDED SIGNUP CANCELLED');
            //console.log('Current Step:', current_step);
            //console.log('Data Object:', data.data);
            /*console.log('Complete Callback Data:', {
              current_step,
              timestamp: new Date().toISOString(),
              event: 'CANCEL'
            });
            console.groupEnd();*/
            
            setEmbeddedSignupData({
              current_step,
              status: 'cancelled'
            });
            
            notify.warning(t('whatsapp.errors.signup_cancelled', { step: current_step }));
          }
        }
      } catch (error) {
        // Not a JSON message - check if it's URL-encoded OAuth callback data
        if (typeof event.data === 'string' && event.data.includes('code=')) {
          console.group('🔐 Mobile OAuth Code Detected');
          
          // Parse URL-encoded data
          const params = new URLSearchParams(event.data);
          const code = params.get('code');
          const domain = params.get('domain');
          const baseDomain = params.get('base_domain');
          
          //console.log('✅ OAuth Code found:', code ? code.substring(0, 50) + '...' : 'none');
          //console.log('Domain:', domain);
          //console.log('Base Domain:', baseDomain);
          console.groupEnd();
          
          if (code) {
            //console.log('%c📱 OAuth Code captured from mobile popup!', 'color: green; font-size: 14px; font-weight: bold;');
            
            // Store the code and show modal to ask for phone number
            setMobileOAuthCode(code);
            setMobilePhoneInput('');
            setMobilePhoneLookupResult(null);
            setShowMobileConfirmation(false);
            setShowMobileCodeModal(true);
          }
        } else {
          //console.log('Received non-JSON message from popup:', event.data?.substring?.(0, 100) || event.data);
        }
      }
    };

    window.addEventListener('message', sessionInfoListener);

    // Mobile fallback: Check for pending OAuth data when window regains focus
    const handleWindowFocus = () => {
      //console.log('📱 Window regained focus - checking for pending OAuth data...');
      
      // Check localStorage for pending signup data (set by Facebook callback)
      const pendingSignup = localStorage.getItem('whatsapp_pending_signup');
      if (pendingSignup) {
        try {
          const data = JSON.parse(pendingSignup);
          //console.log('📱 Found pending signup data:', data);
          localStorage.removeItem('whatsapp_pending_signup');
          
          if (data.phone_number_id && data.waba_id) {
            // Check phone status - if PENDING, show PIN modal
            checkPhoneVerificationStatus(data.phone_number_id, data.waba_id, data);
          }
        } catch (e) {
          console.error('Failed to parse pending signup data:', e);
          localStorage.removeItem('whatsapp_pending_signup');
        }
      }
      
      // Also check URL params (in case page was reloaded)
      const currentParams = new URLSearchParams(window.location.search);
      const currentOauthComplete = currentParams.get('oauth_complete');
      const currentPhonesEncoded = currentParams.get('phones');
      
      if (currentOauthComplete === 'true' && currentPhonesEncoded && !showAgentSelectionModal) {
        try {
          const phonesJson = atob(currentPhonesEncoded);
          const phones = JSON.parse(phonesJson);
          
          //console.log('📱 Found OAuth params on focus:', phones);
          
          const initialSelections = {};
          phones.forEach((phone, index) => {
            initialSelections[phone.phone_number_id] = index === 0 ? 'customer_care' : 'tutor';
          });
          
          setPhonesForAgentSelection(phones);
          setAgentSelections(initialSelections);
          setShowAgentSelectionModal(true);
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (e) {
          console.error('Failed to parse phones on focus:', e);
        }
      }
    };
    
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        handleWindowFocus();
      }
    });

    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    const registered = urlParams.get('registered');
    const oauthComplete = urlParams.get('oauth_complete');
    const phonesEncoded = urlParams.get('phones');
    
    // Handle OAuth completion with phone numbers for agent selection
    if (oauthComplete === 'true' && phonesEncoded) {
      try {
        const phonesJson = atob(phonesEncoded);
        const phones = JSON.parse(phonesJson);
        
        console.group('📱 OAuth Complete - Phone Numbers Received');
        //console.log('Found phones:', phones);
        console.groupEnd();
        
        // Initialize agent selections
        const initialSelections = {};
        phones.forEach((phone, index) => {
          initialSelections[phone.phone_number_id] = index === 0 ? 'customer_care' : 'tutor';
        });
        
        setPhonesForAgentSelection(phones);
        setAgentSelections(initialSelections);
        setShowAgentSelectionModal(true);
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Failed to parse phone data:', e);
        notify.error(t('whatsapp.errors.processing_error'));
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (success) {
      notify.success(t('whatsapp.success.connection_success', { count: registered }));
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchNumbers();
    } else if (error) {
      notify.error(t('whatsapp.errors.connection_failed', { error: decodeURIComponent(error) }));
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('message', sessionInfoListener);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [fetchNumbers, checkPhoneVerificationStatus, processPendingOAuthData]);

  // Request PIN code via SMS or Voice (for OTP verification flow - optional)
  // eslint-disable-next-line no-unused-vars
  const handleRequestPinCode = async () => {
    if (!pendingPhone) return;
    
    setPinRequesting(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/phone-verification/request-code', {
        phone_number_id: pendingPhone.phone_number_id,
        code_method: pinCodeMethod,
        language: 'it'
      });
      
      //console.log('PIN code request response:', response.data);
      setPinCodeSent(true);
      notify.success(t('whatsapp.success.code_sent'));
    } catch (error) {
      console.error('Failed to request PIN code:', error);
      const errorMsg = error.response?.data?.detail || 'Unknown error';
      notify.error(t('whatsapp.errors.pin_error', { error: errorMsg }));
    } finally {
      setPinRequesting(false);
    }
  };

  // Register the phone number with Two-Step Verification PIN
  // This is the FINAL step after Embedded Signup
  // API: POST /{phone_number_id}/register with {"messaging_product": "whatsapp", "pin": "6_digit_pin"}
  const handleRegisterWithPin = async () => {
    if (!pendingPhone || pinCode.length !== 6) {
      notify.warning(t('whatsapp.errors.pin_required'));
      return;
    }
    
    setPinVerifying(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/phone-verification/register', {
        phone_number_id: pendingPhone.phone_number_id,
        pin: pinCode
      });
      
      //console.log('Phone registration response:', response.data);
      
      if (response.data.success) {
        notify.success(t('whatsapp.success.number_registered'));
        setShowPinModal(false);
        
        // Proceed to agent selection
        proceedToAgentSelection(
          pendingPhone.phone_number_id,
          pendingPhone.waba_id,
          {
            display_phone_number: pendingPhone.display_phone_number,
            verified_name: pendingPhone.verified_name
          }
        );
        
        // Reset PIN state
        setPendingPhone(null);
        setPinCode('');
        setPinCodeSent(false);
      }
    } catch (error) {
      console.error('Failed to register phone with PIN:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid PIN';
      notify.error(t('whatsapp.errors.pin_error', { error: errorMsg }));
    } finally {
      setPinVerifying(false);
    }
  };

  // Verify the entered PIN code (for OTP flow - optional)
  // eslint-disable-next-line no-unused-vars
  const handleVerifyPinCode = async () => {
    if (!pendingPhone || pinCode.length !== 6) {
      notify.warning(t('whatsapp.errors.code_required'));
      return;
    }
    
    setPinVerifying(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/phone-verification/verify-code', {
        phone_number_id: pendingPhone.phone_number_id,
        code: pinCode
      });
      
      //console.log('PIN verification response:', response.data);
      
      if (response.data.success) {
        notify.success(t('whatsapp.success.number_verified'));
        setShowPinModal(false);
        
        // Proceed to agent selection
        proceedToAgentSelection(
          pendingPhone.phone_number_id,
          pendingPhone.waba_id,
          {
            display_phone_number: pendingPhone.display_phone_number,
            verified_name: pendingPhone.verified_name
          }
        );
        
        // Reset PIN state
        setPendingPhone(null);
        setPinCode('');
        setPinCodeSent(false);
      }
    } catch (error) {
      console.error('Failed to verify PIN code:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid code';
      notify.error(t('whatsapp.errors.pin_error', { error: errorMsg }));
    } finally {
      setPinVerifying(false);
    }
  };

  // Close PIN modal
  const handleClosePinModal = async () => {
    if (await confirmAsync({ message: t('whatsapp.confirm.cancel_verification') })) {
      setShowPinModal(false);
      setPendingPhone(null);
      setPinCode('');
      setPinCodeSent(false);
    }
  };

  // Mobile OAuth Code Modal functions
  const handleMobilePhoneLookup = async () => {
    if (!mobilePhoneInput.trim()) {
      notify.warning(t('whatsapp.errors.phone_required'));
      return;
    }

    setMobilePhoneLookupLoading(true);
    setMobilePhoneLookupResult(null);
    setShowMobileConfirmation(false);

    try {
      // Use the lookup-from-phone endpoint to find WABA and Phone Number ID
      const response = await apiClient.post('/api/v1/whatsapp/oauth/lookup-from-phone', {
        phone_number: mobilePhoneInput.trim()
      });

      //console.log('📱 Phone lookup result:', response.data);

      if (response.data.success) {
        setMobilePhoneLookupResult({
          phone_number_id: response.data.phone_number_id,
          waba_id: response.data.business_account_id,
          display_phone_number: response.data.display_phone_number,
          verified_name: response.data.verified_name,
          found: true
        });
        // Show confirmation step
        setShowMobileConfirmation(true);
      }
    } catch (error) {
      console.error('Phone lookup failed:', error);
      
      if (error.response?.status === 404) {
        setMobilePhoneLookupResult({
          found: false,
          message: error.response?.data?.detail || 'Numero non trovato nei tuoi WhatsApp Business Account'
        });
      } else {
        notify.error(t('whatsapp.errors.search_error', { error: error.response?.data?.detail || 'Unknown error' }));
      }
    } finally {
      setMobilePhoneLookupLoading(false);
    }
  };

  const handleMobileConfirmAndProceed = async () => {
    if (!mobilePhoneLookupResult?.found) return;

    //console.log('📱 User confirmed - proceeding with phone:', mobilePhoneLookupResult);

    // Close the modal
    setShowMobileCodeModal(false);

    // Check phone verification status and proceed
    checkPhoneVerificationStatus(
      mobilePhoneLookupResult.phone_number_id,
      mobilePhoneLookupResult.waba_id,
      {
        display_phone_number: mobilePhoneLookupResult.display_phone_number,
        verified_name: mobilePhoneLookupResult.verified_name
      }
    );

    // Reset state
    setMobileOAuthCode(null);
    setMobilePhoneInput('');
    setMobilePhoneLookupResult(null);
    setShowMobileConfirmation(false);
  };

  const handleCloseMobileCodeModal = async () => {
    if (await confirmAsync({ message: t('whatsapp.confirm.cancel_config') })) {
      setShowMobileCodeModal(false);
      setMobileOAuthCode(null);
      setMobilePhoneInput('');
      setMobilePhoneLookupResult(null);
      setShowMobileConfirmation(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGenerateQR = async () => {
    setQrLoading(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/oauth/generate-qr');
      const data = response.data;
      
      // Check if we got an embedded signup URL (OAuth flow)
      if (data.embedded_signup_url) {
        // Open OAuth dialog directly - it will handle authentication
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        
        //console.log('📋 Opening OAuth Dialog URL:', data.embedded_signup_url);
        
        const popup = window.open(
          data.embedded_signup_url,
          'WhatsApp Business Setup',
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        );
        
        if (!popup) {
          notify.error(t('whatsapp.errors.allow_popups'));
          setQrLoading(false);
          return;
        }
        
        // Poll for callback completion
        const pollInterval = setInterval(() => {
          if (popup.closed) {
            clearInterval(pollInterval);
            setQrLoading(false);
            fetchNumbers();
          }
        }, 1000);
      } else {
        // QR code flow (if ever supported)
        setQrCodeData(data);
        setShowQrModal(true);
        setQrLoading(false);
      }
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      notify.error(t('whatsapp.errors.start_failed', { error: error.response?.data?.detail || 'Unknown error' }));
      setQrLoading(false);
    }
  };

  const handleCloseQrModal = () => {
    setShowQrModal(false);
    setQrCodeData(null);
  };

  // Separate async function to fetch WABA info after FB login
  // NOTE: Currently unused - sessionInfoListener handles this via embedded signup events
  // Keeping for potential future use or direct FB.api calls
  // eslint-disable-next-line no-unused-vars
  const fetchWabaInfo = async (accessToken, userID) => {
    //console.log('%c📱 Fetching WhatsApp Business accounts...', 'color: blue; font-size: 14px;');
    
    try {
      // Call backend to exchange token and get WABA info
      const wabaResponse = await apiClient.post('/api/v1/whatsapp/oauth/exchange-token', {
        access_token: accessToken,
        user_id: userID
      });
      
      //console.log('📱 WABA Response:', wabaResponse.data);
      
      if (wabaResponse.data.phones && wabaResponse.data.phones.length > 0) {
        const phones = wabaResponse.data.phones;
        
        // Check first phone verification status
        const firstPhone = phones[0];
        if (firstPhone.code_verification_status === 'NOT_VERIFIED') {
          // Phone needs PIN verification
          //console.log('%c⚠️ Phone needs verification', 'color: orange; font-size: 14px;');
          setPendingPhone({
            phone_number_id: firstPhone.phone_number_id,
            waba_id: firstPhone.waba_id || wabaResponse.data.waba_id,
            display_phone_number: firstPhone.display_phone_number,
            verified_name: firstPhone.verified_name || ''
          });
          setShowPinModal(true);
          setPinCodeSent(false);
          setPinCode('');
        } else {
          // Phone is verified - show agent selection
          //console.log('%c✅ Phone is verified, showing agent selection', 'color: green; font-size: 14px;');
          const initialSelections = {};
          phones.forEach((phone, index) => {
            initialSelections[phone.phone_number_id] = index === 0 ? 'customer_care' : 'tutor';
          });
          
          setPhonesForAgentSelection(phones);
          setAgentSelections(initialSelections);
          setShowAgentSelectionModal(true);
        }
      } else {
        //console.log('%c⚠️ No WhatsApp phones found', 'color: orange; font-size: 14px;');
        notify.warning(t('whatsapp.errors.no_numbers_found'));
      }
    } catch (error) {
      console.error('Failed to fetch WABA info:', error);
      notify.error(t('whatsapp.errors.retrieve_failed'));
    }
  };

  // FB.login callback - must NOT be async (Facebook SDK requirement)
  // After Facebook authentication, we show the phone lookup modal so user can enter their number.
  // This is more reliable than waiting for embedded signup events which may not arrive.
  const fbLoginCallback = (response) => {
    console.group('🔐 FB.login Callback Response');
    //console.log('Auth Response:', response);
    //console.log('Status:', response.status);
    //console.log('Auth Response Details:', response.authResponse);
    console.groupEnd();
    
    if (response.authResponse) {
      const accessToken = response.authResponse.accessToken;
      const userID = response.authResponse.userID;
      
      //console.log('%c✅ Successfully Authenticated with Facebook', 'color: green; font-size: 14px; font-weight: bold;');
      //console.log('Access Token:', accessToken);
      //console.log('User ID:', userID);
      
      // Store auth response
      setEmbeddedSignupData({
        fbAuthResponse: response.authResponse,
        flow: 'new_number_setup',
        status: 'authenticated'
      });
      
      // SEMPRE mostra il modal per inserire il numero di telefono
      // L'utente deve inserire il numero → cerchiamo WABA ID e phone_number_id → PIN → selezione agente
      //console.log('%c📱 Showing phone number lookup modal...', 'color: blue; font-size: 14px;');
      setMobilePhoneInput('');
      setMobilePhoneLookupResult(null);
      setShowMobileConfirmation(false);
      setShowMobileCodeModal(true);
    } else {
      //console.log('❌ User cancelled login or did not fully authorize');
      notify.warning(t('whatsapp.errors.login_cancelled'));
    }
  };


  const launchWhatsAppSignup = () => {
    const configId = process.env.REACT_APP_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID;
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin; 
    const redirectUri = `${frontendUrl}/api/v1/whatsapp/oauth/callback`;
    
    if (!configId) {
      notify.error(t('whatsapp.errors.config_error'));
      return;
    }
    
    //console.log('📋 Launching WhatsApp Signup with config_id:', configId);
    //console.log('📋 Redirect URI:', redirectUri);
    
    // FB.login con Embedded Signup
    // redirect_uri deve corrispondere ESATTAMENTE a uno dei callback URL configurati nell'app Facebook
    FB.login(fbLoginCallback, {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      redirect_uri: redirectUri,
      extras: {
        //featureType: 'whatsapp_embedded_signup',
        featureType: 'whatsapp_business_app_onboarding',
        sessionInfoVersion: "3",
        "version":"v3",
        setup: {}
      }
    });
  };

  const handleCloseManualModal = () => {
    setShowManualModal(false);
    setManualForm({
      phoneNumberId: '',
      businessAccountId: '',
      accessToken: '',
      phoneNumber: ''
    });
  };

  const handleManualFormChange = (e) => {
    setManualForm({
      ...manualForm,
      [e.target.name]: e.target.value
    });
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/v1/whatsapp/numbers/manual', manualForm);
      notify.success(t('whatsapp.success.manual_connected'));
      handleCloseManualModal();
      fetchNumbers();
    } catch (error) {
      console.error('Failed to connect number:', error);
      notify.error(t('whatsapp.errors.manual_connect_failed', { error: error.response?.data?.detail || 'Unknown error' }));
    }
  };

  const handleAgentSelectionChange = (phoneNumberId, agentType) => {
    setAgentSelections({
      ...agentSelections,
      [phoneNumberId]: agentType
    });
  };

  const handleSaveWhatsAppNumbers = async () => {
    // Validate that each phone has an agent selected
    for (const phone of phonesForAgentSelection) {
      if (!agentSelections[phone.phone_number_id]) {
        notify.warning(t('whatsapp.errors.select_agent'));
        return;
      }
    }
    
    setSavingNumbers(true);
    try {
      const numbersToSave = phonesForAgentSelection.map(phone => ({
        phone_number_id: phone.phone_number_id,
        business_account_id: phone.waba_id || phone.business_account_id,
        display_phone_number: phone.display_phone_number,
        verified_name: phone.verified_name,
        agent_type: agentSelections[phone.phone_number_id]
      }));
      
      //console.log('Saving WhatsApp numbers with selections:', numbersToSave);
      
      const response = await apiClient.post('/api/v1/whatsapp/save-whatsapp-numbers', {
        numbers: numbersToSave
      });
      
      //console.log('Save response:', response.data);
      notify.success(t('whatsapp.success.numbers_saved', { count: response.data.saved_count }));
      
      setShowAgentSelectionModal(false);
      setPhonesForAgentSelection([]);
      setAgentSelections({});
      setEmbeddedSignupData(null);
      
      // Refresh the numbers list
      fetchNumbers();
    } catch (error) {
      console.error('Failed to save WhatsApp numbers:', error);
      notify.error(t('whatsapp.errors.save_failed', { error: error.response?.data?.detail || 'Unknown error' }));
    } finally {
      setSavingNumbers(false);
    }
  };

  const handleCloseAgentSelectionModal = () => {
    setShowAgentSelectionModal(false);
    setPhonesForAgentSelection([]);
    setAgentSelections({});
  };

  const handleLookupPhoneNumberId = async () => {
    if (!manualForm.phoneNumber) {
      notify.warning(t('whatsapp.errors.phone_required_lookup'));
      return;
    }

    if (!manualForm.businessAccountId) {
      notify.warning(t('whatsapp.errors.waba_required_lookup'));
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/oauth/lookup-phone-number-id', {
        phone_number: manualForm.phoneNumber,
        business_account_id: manualForm.businessAccountId
      });
      
      // Auto-fill the Phone Number ID
      setManualForm({
        ...manualForm,
        phoneNumberId: response.data.phone_number_id
      });
      
      notify.success(t('whatsapp.success.phone_id_found', { phone: response.data.display_phone_number, name: response.data.verified_name, id: response.data.phone_number_id }));
    } catch (error) {
      console.error('Failed to lookup phone number ID:', error);
      const errorMsg = error.response?.data?.detail || 'Unknown error';
      notify.error(t('whatsapp.errors.lookup_failed_detail', { error: errorMsg }));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleAutoLookupAll = async () => {
    if (!manualForm.phoneNumber) {
      notify.warning(t('whatsapp.errors.phone_required_lookup'));
      return;
    }
    
    setAutoLookupLoading(true);
    try {
      const response = await apiClient.post('/api/v1/whatsapp/oauth/lookup-from-phone', {
        phone_number: manualForm.phoneNumber
      });
      
      // Auto-fill BOTH WABA ID and Phone Number ID
      setManualForm({
        ...manualForm,
        businessAccountId: response.data.business_account_id,
        phoneNumberId: response.data.phone_number_id
      });
      
      notify.success(t('whatsapp.success.auto_lookup_success', { phone: response.data.display_phone_number, name: response.data.verified_name, waba: response.data.business_account_id, id: response.data.phone_number_id }));
    } catch (error) {
      console.error('Failed to auto-lookup:', error);
      const errorMsg = error.response?.data?.detail || 'Unknown error';
      notify.error(t('whatsapp.errors.auto_lookup_failed', { error: errorMsg }));
    } finally {
      setAutoLookupLoading(false);
    }
  };

  const handleDelete = async (numberId, pin = null) => {
    if (!pin && !(await confirmAsync({ message: t('whatsapp.confirm.disconnect_number'), danger: true }))) return;
    try {
      const url = pin 
        ? `/api/v1/whatsapp/numbers/${numberId}?pin=${encodeURIComponent(pin)}`
        : `/api/v1/whatsapp/numbers/${numberId}`;
      await apiClient.delete(url);
      setShowDeregisterPinModal(false);
      setDeregisterPinCode('');
      setDeregisterNumberId(null);
      fetchNumbers();
      notify.success(t('whatsapp.success.number_disconnected'));
    } catch (error) {
      console.error('Failed to delete number:', error);
      const errorDetail = error.response?.data?.detail;
      
      if (errorDetail === 'PIN_REQUIRED') {
        // PIN not in database, show modal to request it
        setDeregisterNumberId(numberId);
        setShowDeregisterPinModal(true);
      } else if (errorDetail === 'INVALID_PIN') {
        notify.error(t('whatsapp.errors.invalid_pin', 'Invalid PIN. Please check and try again.'));
      } else {
        notify.error(t('whatsapp.errors.disconnect_failed'));
      }
    }
  };

  const handleDeregisterWithPin = async () => {
    if (!deregisterNumberId || deregisterPinCode.length !== 6) return;
    setDeregisterLoading(true);
    try {
      await handleDelete(deregisterNumberId, deregisterPinCode);
    } finally {
      setDeregisterLoading(false);
    }
  };

  const handleCloseDeregisterPinModal = () => {
    setShowDeregisterPinModal(false);
    setDeregisterPinCode('');
    setDeregisterNumberId(null);
  };

  const toggleActive = async (number) => {
    try {
      await apiClient.patch(`/api/v1/whatsapp/numbers/${number.id}`, {
        is_active: !number.is_active
      });
      fetchNumbers();
    } catch (error) {
      console.error('Failed to update number:', error);
    }
  };

  // ============================================================================
  // BUSINESS PROFILE HANDLERS
  // ============================================================================
  
  const toggleProfileExpand = (numberId) => {
    if (expandedProfileId === numberId) {
      setExpandedProfileId(null);
    } else {
      setExpandedProfileId(numberId);
      // Initialize profile form with existing data
      const number = numbers.find(n => n.id === numberId);
      if (number && !profileForms[numberId]) {
        setProfileForms(prev => ({
          ...prev,
          [numberId]: {
            description: number.profile_description || '',
            website_url: number.profile_website_url || '',
            email: number.profile_email || ''
          }
        }));
      }
    }
  };

  const handleProfileFormChange = (numberId, field, value) => {
    setProfileForms(prev => ({
      ...prev,
      [numberId]: {
        ...prev[numberId],
        [field]: value
      }
    }));
  };

  const saveProfileSettings = async (numberId) => {
    const form = profileForms[numberId];
    if (!form) return;

    // Validate description length
    if (form.description && form.description.length > 256) {
      notify.warning(t('whatsapp.profile.error_description_too_long', 'Description must be 256 characters or less'));
      return;
    }

    // Validate HTTPS URL
    if (form.website_url && !form.website_url.startsWith('https://')) {
      notify.warning(t('whatsapp.profile.error_url_https', 'Website URL must start with https://'));
      return;
    }

    // Validate email format
    if (form.email && !form.email.includes('@')) {
      notify.warning(t('whatsapp.profile.error_invalid_email', 'Please enter a valid email address'));
      return;
    }

    setProfileSaving(prev => ({ ...prev, [numberId]: true }));

    try {
      await apiClient.put(`/api/v1/whatsapp/numbers/${numberId}/profile`, {
        description: form.description || null,
        website_url: form.website_url || null,
        email: form.email || null
      });
      fetchNumbers();
      notify.success(t('whatsapp.profile.save_success', 'Profile settings saved!'));
    } catch (error) {
      console.error('Failed to save profile:', error);
      const errorMsg = error.response?.data?.detail || t('whatsapp.profile.save_error', 'Failed to save profile');
      notify.error(errorMsg);
    } finally {
      setProfileSaving(prev => ({ ...prev, [numberId]: false }));
    }
  };

  const handlePhotoUpload = async (numberId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      notify.warning(t('whatsapp.profile.error_photo_type', 'Only JPEG and PNG images are allowed'));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      notify.warning(t('whatsapp.profile.error_photo_size', 'Photo must be smaller than 5MB'));
      return;
    }

    setPhotoUploading(prev => ({ ...prev, [numberId]: true }));

    try {
      const formData = new FormData();
      formData.append('photo', file);

      await apiClient.post(`/api/v1/whatsapp/numbers/${numberId}/profile/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchNumbers();
      notify.success(t('whatsapp.profile.photo_upload_success', 'Profile photo uploaded!'));
    } catch (error) {
      console.error('Failed to upload photo:', error);
      const errorMsg = error.response?.data?.detail || t('whatsapp.profile.photo_upload_error', 'Failed to upload photo');
      notify.error(errorMsg);
    } finally {
      setPhotoUploading(prev => ({ ...prev, [numberId]: false }));
    }
  };

  const deleteProfilePhoto = async (numberId) => {
    if (!(await confirmAsync({ message: t('whatsapp.profile.confirm_delete_photo', 'Delete profile photo?'), danger: true }))) {
      return;
    }

    try {
      await apiClient.delete(`/api/v1/whatsapp/numbers/${numberId}/profile/photo`);
      fetchNumbers();
      notify.success(t('whatsapp.profile.photo_delete_success', 'Profile photo deleted!'));
    } catch (error) {
      console.error('Failed to delete photo:', error);
      notify.error(t('whatsapp.profile.photo_delete_error', 'Failed to delete photo'));
    }
  };

  const syncProfileToWhatsApp = async (numberId) => {
    if (!(await confirmAsync({ message: t('whatsapp.profile.confirm_sync', 'Sync profile to WhatsApp? This will update your public business profile.') }))) {
      return;
    }

    setProfileSyncing(prev => ({ ...prev, [numberId]: true }));

    try {
      const response = await apiClient.post(`/api/v1/whatsapp/numbers/${numberId}/profile/sync`);
      fetchNumbers();
      
      let message = t('whatsapp.profile.sync_success', 'Profile synced to WhatsApp!');
      const hasWarnings = response.data.warnings && response.data.warnings.length > 0;
      if (hasWarnings) {
        message += '\n\n' + t('whatsapp.profile.sync_warnings', 'Warnings:') + '\n' + response.data.warnings.join('\n');
      }
      if (hasWarnings) {
        notify.warning(message);
      } else {
        notify.success(message);
      }
    } catch (error) {
      console.error('Failed to sync profile:', error);
      const errorMsg = error.response?.data?.detail || t('whatsapp.profile.sync_error', 'Failed to sync profile to WhatsApp');
      notify.error(errorMsg);
    } finally {
      setProfileSyncing(prev => ({ ...prev, [numberId]: false }));
    }
  };

  // Combined save and sync function
  const saveAndSyncProfile = async (numberId) => {
    const form = profileForms[numberId];
    if (!form) return;

    // Validate description length
    if (form.description && form.description.length > 256) {
      notify.warning(t('whatsapp.profile.error_description_too_long', 'Description must be 256 characters or less'));
      return;
    }

    // Validate HTTPS URL
    if (form.website_url && !form.website_url.startsWith('https://')) {
      notify.warning(t('whatsapp.profile.error_url_https', 'Website URL must start with https://'));
      return;
    }

    // Validate email format
    if (form.email && !form.email.includes('@')) {
      notify.warning(t('whatsapp.profile.error_invalid_email', 'Please enter a valid email address'));
      return;
    }

    if (!(await confirmAsync({ message: t('whatsapp.profile.confirm_save_sync', 'Save and sync profile to WhatsApp? This will update your public business profile.') }))) {
      return;
    }

    setProfileSaving(prev => ({ ...prev, [numberId]: true }));

    try {
      // Step 1: Save to database
      await apiClient.put(`/api/v1/whatsapp/numbers/${numberId}/profile`, {
        description: form.description || null,
        website_url: form.website_url || null,
        email: form.email || null
      });

      // Step 2: Sync to WhatsApp
      const response = await apiClient.post(`/api/v1/whatsapp/numbers/${numberId}/profile/sync`);
      fetchNumbers();
      
      let message = t('whatsapp.profile.save_sync_success', 'Profile saved and synced to WhatsApp!');
      const hasWarnings = response.data.warnings && response.data.warnings.length > 0;
      if (hasWarnings) {
        message += '\n\n' + t('whatsapp.profile.sync_warnings', 'Warnings:') + '\n' + response.data.warnings.join('\n');
      }
      if (hasWarnings) {
        notify.warning(message);
      } else {
        notify.success(message);
      }
    } catch (error) {
      console.error('Failed to save and sync profile:', error);
      const errorMsg = error.response?.data?.detail || t('whatsapp.profile.save_sync_error', 'Failed to save and sync profile');
      notify.error(errorMsg);
    } finally {
      setProfileSaving(prev => ({ ...prev, [numberId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">{t('whatsapp.loading')}</div>
      </div>
    );
  }

  const hasCustomerCare = numbers.some(n => n.agent_type === 'customer_care');
  const hasTutor = numbers.some(n => n.agent_type === 'tutor');
  const canAddMore = !hasCustomerCare || !hasTutor;

  return (
    <div className="container whatsapp-numbers">
      <div className="page-header">
        <div className="title-with-back">
          <button className="back-button" onClick={() => navigate('/dashboard')} title={t('common.back_to_dashboard')}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <h1>{t('whatsapp.page_title')}</h1>
        </div>

               
        {canAddMore && (
          <button 
            className="btn-primary" 
            onClick={launchWhatsAppSignup} 
            disabled={qrLoading}
            style={{
              fontSize: '18px',
              padding: '16px 32px',
              minWidth: '220px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
              borderRadius: '12px'
            }}
          >
            {qrLoading ? `🔄 ${t('whatsapp.buttons.opening_oauth')}` : `🔗 ${t('whatsapp.buttons.connect_new')}`}
          </button>
        )}
        
        
        
      </div>

      <div className="info-banner">
        <h3>{t('whatsapp.info_banner.title')}</h3>
        <p>{t('whatsapp.info_banner.description')}</p>
        <ul>
          <li><strong>{t('whatsapp.info_banner.customer_care')}</strong></li>
          <li><strong>{t('whatsapp.info_banner.tutor')}</strong></li>
        </ul>
        <p style={{marginTop: '15px', fontSize: '14px'}}>
          <strong>🔗 {t('whatsapp.buttons.connect_new')} (OAuth):</strong> {t('whatsapp.quick_setup_hint')}
        </p>
      </div>

      {/* Agent Selection Modal */}
      {showAgentSelectionModal && phonesForAgentSelection.length > 0 && (
        <div className="modal-overlay overlay-agent-selection" onClick={handleCloseAgentSelectionModal}>
          <div className="modal-content agent-selection-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🤖 {t('whatsapp.agent_selection.title')}</h2>
              <button className="close-button" onClick={handleCloseAgentSelectionModal}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                {t('whatsapp.agent_selection.description')}
              </p>
              
              <div className="phones-list">
                {phonesForAgentSelection.map((phone, index) => (
                  <div key={phone.phone_number_id} className="phone-card">
                    <div className="phone-info">
                      <strong>📱 {t('whatsapp.number_label', { index: index + 1 })}</strong>
                      <p>
                        {phone.display_phone_number}
                        {phone.verified_name && <> • {phone.verified_name}</>}
                      </p>
                    </div>
                    
                    <div className="agent-options">
                      <label className={`agent-option ${agentSelections[phone.phone_number_id] === 'customer_care' ? 'selected customer-care' : ''}`}>
                        <input
                          type="radio"
                          name={`agent-${phone.phone_number_id}`}
                          value="customer_care"
                          checked={agentSelections[phone.phone_number_id] === 'customer_care'}
                          onChange={(e) => handleAgentSelectionChange(phone.phone_number_id, e.target.value)}
                        />
                        <strong>👨‍💼 {t('whatsapp.agent_selection.customer_care_title')}</strong>
                        <span className="agent-desc">{t('whatsapp.agent_selection.customer_care_desc')}</span>
                      </label>
                      
                      <label className={`agent-option ${agentSelections[phone.phone_number_id] === 'tutor' ? 'selected tutor' : ''}`}>
                        <input
                          type="radio"
                          name={`agent-${phone.phone_number_id}`}
                          value="tutor"
                          checked={agentSelections[phone.phone_number_id] === 'tutor'}
                          onChange={(e) => handleAgentSelectionChange(phone.phone_number_id, e.target.value)}
                        />
                        <strong>🎓 {t('whatsapp.agent_selection.tutor_title')}</strong>
                        <span className="agent-desc">{t('whatsapp.agent_selection.tutor_desc')}</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="modal-actions">
                <button 
                  className="btn-secondary" 
                  onClick={handleCloseAgentSelectionModal}
                  disabled={savingNumbers}
                >
                  {t('whatsapp.buttons.cancel')}
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSaveWhatsAppNumbers}
                  disabled={savingNumbers}
                >
                  {savingNumbers ? `💾 ${t('whatsapp.agent_selection.saving')}` : `✅ ${t('whatsapp.agent_selection.save_config')}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIN Modal removed - Auto-registration handles PIN generation automatically */}

      {/* Deregistration PIN Modal */}
      {showDeregisterPinModal && (
        <div className="modal-overlay overlay-deregister-pin" onClick={handleCloseDeregisterPinModal}>
          <div className="modal-content pin-verification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔐 {t('whatsapp.deregister_pin_modal.title', 'PIN Required')}</h2>
              <button className="close-button" onClick={handleCloseDeregisterPinModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="pin-info-banner" style={{background: '#fff3e0', border: '1px solid #ff9800', marginBottom: '20px', padding: '15px', borderRadius: '8px'}}>
                <p style={{margin: 0, color: '#e65100'}}>
                  ⚠️ {t('whatsapp.deregister_pin_modal.description', 'To disconnect this WhatsApp number, please enter your 6-digit Two-Step Verification PIN.')}
                </p>
              </div>

              <div className="pin-verify-section">
                <h4>🔒 {t('whatsapp.deregister_pin_modal.pin_title', 'Two-Step Verification PIN')}</h4>
                <p style={{fontSize: '14px', color: '#666'}}>
                  {t('whatsapp.deregister_pin_modal.pin_hint', 'This is the 6-digit PIN you set up in WhatsApp Business settings.')}
                </p>
                
                <div className="pin-input-container" style={{margin: '20px 0'}}>
                  <input
                    type="password"
                    className="pin-input"
                    maxLength="6"
                    value={deregisterPinCode}
                    onChange={(e) => setDeregisterPinCode(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('whatsapp.pin_modal.pin_placeholder', '000000')}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '15px',
                      fontSize: '24px',
                      textAlign: 'center',
                      letterSpacing: '10px',
                      border: '2px solid #ddd',
                      borderRadius: '8px'
                    }}
                  />
                </div>
                
                <button 
                  className="btn-primary btn-full-width"
                  onClick={handleDeregisterWithPin}
                  disabled={deregisterLoading || deregisterPinCode.length !== 6}
                  style={{width: '100%', padding: '12px', marginTop: '10px'}}
                >
                  {deregisterLoading 
                    ? `🔄 ${t('whatsapp.deregister_pin_modal.disconnecting', 'Disconnecting...')}` 
                    : `🗑️ ${t('whatsapp.deregister_pin_modal.disconnect_button', 'Disconnect Number')}`}
                </button>
              </div>

              <div className="modal-actions" style={{marginTop: '20px'}}>
                <button 
                  className="btn-secondary" 
                  onClick={handleCloseDeregisterPinModal}
                  style={{width: '100%'}}
                >
                  ❌ {t('whatsapp.buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQrModal && qrCodeData && (
        <div className="modal-overlay" onClick={handleCloseQrModal}>
          <div className="modal-content qr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('whatsapp.qr_modal.title')}</h2>
              <button className="close-button" onClick={handleCloseQrModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="qr-code-container">
                {qrCodeData.qr_code_svg && (
                  <div dangerouslySetInnerHTML={{ __html: qrCodeData.qr_code_svg }} />
                )}
                {qrCodeData.qr_code_url && !qrCodeData.qr_code_svg && (
                  <img src={qrCodeData.qr_code_url} alt="QR Code" style={{maxWidth: '100%'}} />
                )}
              </div>
              <div className="qr-instructions">
                <h4>{t('whatsapp.qr_modal.instructions_title')}</h4>
                <ol>
                  <li>{t('whatsapp.qr_modal.step1')}</li>
                  <li>{t('whatsapp.qr_modal.step2')}</li>
                  <li>{t('whatsapp.qr_modal.step3')}</li>
                  <li>{t('whatsapp.qr_modal.step4')}</li>
                  <li>{t('whatsapp.qr_modal.step5')}</li>
                </ol>
                <p style={{marginTop: '15px', padding: '10px', background: '#fff3cd', borderRadius: '5px'}}>
                  {t('whatsapp.qr_modal.assigns_to')} <strong>{qrCodeData.agent_type === 'customer_care' ? t('whatsapp.number_card.customer_care') : t('whatsapp.number_card.tutor')}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {numbers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📱</div>
          <h3>{t('whatsapp.empty_state.title')}</h3>
          <p>{t('whatsapp.empty_state.subtitle')}</p>
          
          <div style={{marginTop: '30px', display: 'grid', gap: '20px', maxWidth: '900px', margin: '30px auto'}}>
            {/* Connect New Number */}
            <div style={{padding: '20px', background: '#e3f2fd', borderRadius: '8px', border: '2px solid #2196f3'}}>
              <h4 style={{marginTop: 0, color: '#1976d2'}}>🔗 {t('whatsapp.empty_state.connect_new_title')}</h4>
              <p style={{fontSize: '14px', marginBottom: '10px'}}>
                <strong>{t('whatsapp.empty_state.connect_new_best_for')}</strong> {t('whatsapp.empty_state.connect_new_desc')}
              </p>
              <ul style={{fontSize: '14px', lineHeight: '1.6', marginLeft: '20px'}}>
                <li>{t('whatsapp.empty_state.connect_new_feature1')}</li>
                <li>{t('whatsapp.empty_state.connect_new_feature2')}</li>
                <li>{t('whatsapp.empty_state.connect_new_feature3')}</li>
              </ul>
              <p style={{fontSize: '13px', marginTop: '10px', color: '#1565c0'}}>
                {t('whatsapp.empty_state.connect_new_hint')}
              </p>
            </div>
          </div>
          
          <div className="prerequisites">
            <h4>{t('whatsapp.empty_state.prerequisites_title')}</h4>
            <ul>
              <li>{t('whatsapp.empty_state.prerequisite1')}</li>
              <li>{t('whatsapp.empty_state.prerequisite2')}</li>
              <li>{t('whatsapp.empty_state.prerequisite3')}</li>
            </ul>
            <h4 style={{marginTop: '15px'}}>{t('whatsapp.empty_state.resources_title')}</h4>
            <ul>
              <li><a href="https://business.facebook.com/latest/whatsapp_manager" target="_blank" rel="noopener noreferrer">{t('whatsapp.empty_state.link_whatsapp_manager')}</a></li>
              <li><a href="https://developers.facebook.com/docs/whatsapp/embedded-signup" target="_blank" rel="noopener noreferrer">{t('whatsapp.empty_state.link_embedded_signup')}</a></li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="numbers-grid">
          {numbers.map((number) => (
            <div key={number.id} className="number-card">
              <div className="card-header">
                <div className="agent-type-badge" data-type={number.agent_type}>
                  {number.agent_type === 'customer_care' ? t('whatsapp.number_card.customer_care') : t('whatsapp.number_card.tutor')}
                </div>
                <div className="status-badge" data-active={number.is_active}>
                  {number.is_active ? t('whatsapp.number_card.active') : t('whatsapp.number_card.inactive')}
                </div>
              </div>
              <div className="card-body">
                <h3>{number.agent_name || number.agent_type}</h3>
                <div className="number-info">
                  <div className="info-row">
                    <span className="label">{t('whatsapp.number_card.phone')}</span>
                    <span className="value">{number.phone_number}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">{t('whatsapp.number_card.number_id')}</span>
                    <span className="value code">{number.phone_number_id}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">{t('whatsapp.number_card.waba_id')}</span>
                    <span className="value code">{number.business_account_id}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">{t('whatsapp.number_card.webhook')}</span>
                    <span className={`value ${number.is_webhook_verified ? 'verified' : 'unverified'}`}>
                      {number.is_webhook_verified ? t('whatsapp.number_card.webhook_verified') : t('whatsapp.number_card.webhook_not_verified')}
                    </span>
                  </div>
                  {number.last_webhook_received && (
                    <div className="info-row">
                      <span className="label">{t('whatsapp.number_card.last_activity')}</span>
                      <span className="value">{new Date(number.last_webhook_received).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Profile Settings Expandable Section */}
              <div className="profile-settings-section">
                <button 
                  className="profile-toggle-btn"
                  onClick={() => toggleProfileExpand(number.id)}
                >
                  <span className="profile-toggle-icon">{expandedProfileId === number.id ? '▼' : '▶'}</span>
                  <span><i className="bi bi-gear-fill"></i> {t('whatsapp.profile.settings_title', 'Profile Settings')}</span>
                  {number.profile_synced_at && (
                    <span className="profile-sync-badge synced">
                      ✓ {t('whatsapp.profile.synced', 'Synced')}
                    </span>
                  )}
                </button>
                
                {expandedProfileId === number.id && (
                  <div className="profile-settings-content">
                    {/* Profile Photo */}
                    <div className="profile-photo-section">
                      <label>{t('whatsapp.profile.photo_label', 'Profile Photo')}</label>
                      <div className="profile-photo-container">
                        {number.profile_photo_url ? (
                          <div className="profile-photo-preview">
                            <img 
                              src={number.profile_photo_url} 
                              alt={t('whatsapp.profile.photo_alt', 'Profile')} 
                            />
                            <button 
                              className="btn-small btn-danger"
                              onClick={() => deleteProfilePhoto(number.id)}
                            >
                              {t('whatsapp.profile.delete_photo', 'Delete')}
                            </button>
                          </div>
                        ) : (
                          <div className="profile-photo-placeholder">
                            <span>📷</span>
                            <small>{t('whatsapp.profile.no_photo', 'No photo')}</small>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={el => fileInputRefs.current[number.id] = el}
                          onChange={(e) => handlePhotoUpload(number.id, e)}
                          accept="image/jpeg,image/png"
                          style={{ display: 'none' }}
                        />
                        <button
                          className="btn-small btn-secondary"
                          onClick={() => fileInputRefs.current[number.id]?.click()}
                          disabled={photoUploading[number.id]}
                        >
                          {photoUploading[number.id] 
                            ? t('whatsapp.profile.uploading', 'Uploading...') 
                            : t('whatsapp.profile.upload_photo', 'Upload Photo')}
                        </button>
                      </div>
                      <small className="profile-hint">{t('whatsapp.profile.photo_hint', 'Max 5MB, JPEG or PNG')}</small>
                    </div>

                    {/* Description */}
                    <div className="profile-form-group">
                      <label>
                        {t('whatsapp.profile.description_label', 'Description')}
                        <span className="char-count">
                          {(profileForms[number.id]?.description || '').length}/256
                        </span>
                      </label>
                      <textarea
                        value={profileForms[number.id]?.description || ''}
                        onChange={(e) => handleProfileFormChange(number.id, 'description', e.target.value)}
                        placeholder={t('whatsapp.profile.description_placeholder', 'Describe your business...')}
                        maxLength={256}
                        rows={3}
                      />
                      <small className="profile-hint">
                        {t('whatsapp.profile.description_hint', 'This will appear on your WhatsApp Business profile. Max 256 characters.')}
                      </small>
                    </div>

                    {/* Website URL */}
                    <div className="profile-form-group">
                      <label>{t('whatsapp.profile.website_label', 'Website URL')}</label>
                      <input
                        type="url"
                        value={profileForms[number.id]?.website_url || ''}
                        onChange={(e) => handleProfileFormChange(number.id, 'website_url', e.target.value)}
                        placeholder="https://www.example.com"
                      />
                      <small className="profile-hint">
                        {t('whatsapp.profile.website_hint', 'Must start with https://')}
                      </small>
                    </div>

                    {/* Business Email */}
                    <div className="profile-form-group">
                      <label>{t('whatsapp.profile.email_label', 'Business Email')}</label>
                      <input
                        type="email"
                        value={profileForms[number.id]?.email || ''}
                        onChange={(e) => handleProfileFormChange(number.id, 'email', e.target.value)}
                        placeholder="info@yourbusiness.com"
                      />
                    </div>

                    {/* Sync Status & Last Synced */}
                    {number.profile_synced_at && (
                      <div className="profile-sync-info">
                        <span>✅ {t('whatsapp.profile.last_synced', 'Last synced')}: {new Date(number.profile_synced_at).toLocaleString()}</span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="profile-actions">
                      <button
                        className="btn-primary"
                        onClick={() => saveAndSyncProfile(number.id)}
                        disabled={profileSaving[number.id]}
                        style={{ background: '#2196f3' }}
                        title={t('whatsapp.profile.save_sync_title', 'Save settings and sync to WhatsApp Business Profile')}
                      >
                        {profileSaving[number.id] 
                          ? t('whatsapp.profile.saving_syncing', 'Saving & Syncing...') 
                          : t('whatsapp.profile.save_and_sync', '💾 Save & Sync to WhatsApp')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="card-actions">
                <button className={`btn-toggle ${number.is_active ? 'active' : ''}`} onClick={() => toggleActive(number)}>
                  {number.is_active ? t('whatsapp.buttons.disable') : t('whatsapp.buttons.enable')}
                </button>
                <button className="btn-danger" onClick={() => handleDelete(number.id)}>
                  {t('whatsapp.buttons.disconnect')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Manual Connection Modal */}
      {showManualModal && (
        <div className="modal-overlay" onClick={handleCloseManualModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚙️ {t('whatsapp.manual_modal.title')}</h2>
              <button className="close-button" onClick={handleCloseManualModal}>×</button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom: '25px', padding: '20px', background: '#e3f2fd', borderRadius: '8px', border: '2px solid #2196f3'}}>
                <h3 style={{marginTop: 0, color: '#1976d2'}}>💡 {t('whatsapp.manual_modal.options_title')}</h3>
                <div style={{fontSize: '14px', lineHeight: '1.8'}}>
                  <p><strong>🚀 {t('whatsapp.manual_modal.recommended_method')}</strong> {t('whatsapp.manual_modal.recommended_hint')}</p>
                  <hr style={{margin: '15px 0', borderColor: '#90caf9'}} />
                  <p><strong>⚙️ {t('whatsapp.manual_modal.advanced_options')}</strong></p>
                  <ul style={{marginLeft: '20px'}}>
                    <li><strong>{t('whatsapp.manual_modal.auto_search')}</strong></li>
                    <li><strong>{t('whatsapp.manual_modal.manual_setup')}</strong></li>
                  </ul>
                </div>
              </div>
              
              <p style={{marginBottom: '20px', color: '#666'}}>
                {t('whatsapp.manual_modal.already_activated')}
              </p>
              <div style={{marginBottom: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '5px', border: '2px solid #4caf50'}}>
                <strong>🚀 {t('whatsapp.manual_modal.super_auto_title')}</strong>
                <p style={{fontSize: '14px', marginTop: '5px', marginBottom: 0}}>
                  {t('whatsapp.manual_modal.super_auto_desc')}
                </p>
              </div>
              <div style={{marginBottom: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '5px'}}>
                <strong>🔍 {t('whatsapp.manual_modal.semi_auto_title')}</strong>
                <p style={{fontSize: '14px', marginTop: '5px', marginBottom: 0}}>
                  {t('whatsapp.manual_modal.semi_auto_desc')}
                </p>
              </div>
              <div style={{marginBottom: '20px', padding: '15px', background: '#fff3e0', borderRadius: '5px'}}>
                <strong>⚙️ {t('whatsapp.manual_modal.manual_title')}</strong>
                <p style={{fontSize: '14px', marginTop: '5px', marginBottom: 0}}>
                  {t('whatsapp.manual_modal.manual_desc')}
                </p>
              </div>
              <form onSubmit={handleManualSubmit}>
                <div className="form-group">
                  <label>📱 {t('whatsapp.manual_modal.form.phone_label')}</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={manualForm.phoneNumber}
                    onChange={handleManualFormChange}
                    placeholder={t('whatsapp.manual_modal.form.phone_placeholder')}
                    required
                  />
                  <small>{t('whatsapp.manual_modal.form.phone_hint')}</small>
                </div>

                <div style={{marginBottom: '25px', padding: '20px', background: '#f1f8e9', borderRadius: '8px', textAlign: 'center', border: '2px dashed #4caf50'}}>
                  <button 
                    type="button" 
                    className="btn-primary"
                    onClick={handleAutoLookupAll}
                    disabled={autoLookupLoading || !manualForm.phoneNumber}
                    style={{
                      marginBottom: '10px',
                      fontSize: '16px',
                      padding: '12px 24px',
                      background: autoLookupLoading ? '#999' : '#4caf50',
                      border: 'none'
                    }}
                  >
                    {autoLookupLoading ? `🔄 ${t('whatsapp.buttons.searching_waba')}` : `🚀 ${t('whatsapp.buttons.find_all_auto')}`}
                  </button>
                  <p style={{fontSize: '13px', color: '#558b2f', margin: 0, fontWeight: 'bold'}}>
                    ⬆️ {t('whatsapp.manual_modal.form.auto_hint')}
                  </p>
                </div>

                <div style={{marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '5px'}}>
                  <p style={{fontSize: '13px', color: '#666', marginBottom: '10px', textAlign: 'center'}}>
                    <strong>{t('whatsapp.manual_modal.form.or_manual')}</strong>
                  </p>
                </div>
                
                <div className="form-group">
                  <label>{t('whatsapp.manual_modal.form.waba_label')} {!manualForm.businessAccountId && t('whatsapp.manual_modal.form.waba_optional')}</label>
                  <input
                    type="text"
                    name="businessAccountId"
                    value={manualForm.businessAccountId}
                    onChange={handleManualFormChange}
                    placeholder={t('whatsapp.manual_modal.form.waba_placeholder')}
                    readOnly={autoLookupLoading}
                  />
                  <small>{t('whatsapp.manual_modal.form.waba_hint')}</small>
                </div>

                <div style={{marginBottom: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '5px', textAlign: 'center'}}>
                  <button 
                    type="button" 
                    className="btn-secondary"
                    onClick={handleLookupPhoneNumberId}
                    disabled={lookupLoading || !manualForm.phoneNumber || !manualForm.businessAccountId}
                    style={{marginBottom: '10px'}}
                  >
                    {lookupLoading ? `🔄 ${t('whatsapp.buttons.searching')}` : `🔍 ${t('whatsapp.buttons.find_phone_id')}`}
                  </button>
                  <p style={{fontSize: '13px', color: '#666', margin: 0}}>
                    {t('whatsapp.manual_modal.form.find_phone_hint')}
                  </p>
                </div>
                
                <div className="form-group">
                  <label>{t('whatsapp.manual_modal.form.phone_id_label')} {!manualForm.phoneNumberId && t('whatsapp.manual_modal.form.phone_id_optional')}</label>
                  <input
                    type="text"
                    name="phoneNumberId"
                    value={manualForm.phoneNumberId}
                    onChange={handleManualFormChange}
                    placeholder={t('whatsapp.manual_modal.form.phone_id_placeholder')}
                    pattern="[0-9]{10,20}"
                    title={t('whatsapp.manual_modal.form.phone_id_title')}
                    readOnly={lookupLoading || autoLookupLoading}
                  />
                  <small style={{color: manualForm.phoneNumberId ? '#2e7d32' : '#d32f2f'}}>
                    {manualForm.phoneNumberId 
                      ? `✅ ${t('whatsapp.manual_modal.form.phone_id_ready')}` 
                      : t('whatsapp.manual_modal.form.phone_id_use_auto')
                    }
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('whatsapp.manual_modal.form.access_token_label')}</label>
                  <input
                    type="password"
                    name="accessToken"
                    value={manualForm.accessToken}
                    onChange={handleManualFormChange}
                    placeholder={t('whatsapp.manual_modal.form.access_token_placeholder')}
                    required
                  />
                  <small>{t('whatsapp.manual_modal.form.access_token_hint')}</small>
                </div>
                <div className="form-group">
                  <label>{t('whatsapp.manual_modal.form.display_phone_label')}</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={manualForm.phoneNumber}
                    onChange={handleManualFormChange}
                    placeholder={t('whatsapp.manual_modal.form.display_phone_placeholder')}
                  />
                  <small>{t('whatsapp.manual_modal.form.display_phone_hint')}</small>
                </div>
                <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <button type="button" className="btn-secondary" onClick={handleCloseManualModal}>
                    {t('whatsapp.buttons.cancel')}
                  </button>
                  <button type="submit" className="btn-primary">
                    {t('whatsapp.buttons.connect_number')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {numbers.length > 0 && canAddMore && (
        <div style={{marginTop: '30px', padding: '20px', background: '#e3f2fd', borderRadius: '8px', textAlign: 'center'}}>
          <p><strong>{t('whatsapp.add_more.title')}</strong></p>
          <p style={{marginTop: '10px', fontSize: '14px'}}>
            {t('whatsapp.add_more.click_hint')} {hasCustomerCare ? t('whatsapp.add_more.tutor_agent') : t('whatsapp.add_more.customer_care_agent')}.
          </p>
        </div>
      )}

      {/* Phone Number Lookup Modal - after Facebook OAuth authentication */}
      {showMobileCodeModal && (
        <div className="modal-overlay overlay-mobile" onClick={(e) => e.target === e.currentTarget && handleCloseMobileCodeModal()}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📱 {t('whatsapp.mobile_modal.title')}</h2>
              <button className="close-button" onClick={handleCloseMobileCodeModal}>×</button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '8px'}}>
                <p style={{margin: 0, color: '#2e7d32'}}>
                  <strong>✅ {t('whatsapp.mobile_modal.auth_complete')}</strong>
                </p>
                <p style={{margin: '10px 0 0', fontSize: '14px', color: '#558b2f'}}>
                  {t('whatsapp.mobile_modal.enter_number_hint')}
                </p>
              </div>

              {!showMobileConfirmation ? (
                <>
                  <div className="form-group">
                    <label><strong>📞 {t('whatsapp.mobile_modal.phone_label')}</strong></label>
                    <input
                      type="text"
                      value={mobilePhoneInput}
                      onChange={(e) => setMobilePhoneInput(e.target.value)}
                      placeholder={t('whatsapp.mobile_modal.phone_placeholder')}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: '2px solid #ddd',
                        borderRadius: '8px'
                      }}
                      disabled={mobilePhoneLookupLoading}
                    />
                    <small style={{color: '#666', marginTop: '5px', display: 'block'}}>
                      {t('whatsapp.mobile_modal.phone_hint')}
                    </small>
                  </div>

                  {mobilePhoneLookupResult && !mobilePhoneLookupResult.found && (
                    <div style={{marginTop: '15px', padding: '15px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffb74d'}}>
                      <p style={{margin: 0, color: '#e65100'}}>
                        <strong>⚠️ {t('whatsapp.mobile_modal.number_not_found')}</strong>
                      </p>
                      <p style={{margin: '10px 0 0', fontSize: '14px', color: '#f57c00'}}>
                        {mobilePhoneLookupResult.message}
                      </p>
                      <p style={{margin: '10px 0 0', fontSize: '13px', color: '#666'}}>
                        {t('whatsapp.mobile_modal.ensure_correct')}
                      </p>
                    </div>
                  )}

                  <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                    <button 
                      type="button" 
                      className="btn-secondary" 
                      onClick={handleCloseMobileCodeModal}
                    >
                      {t('whatsapp.buttons.cancel')}
                    </button>
                    <button 
                      type="button" 
                      className="btn-primary" 
                      onClick={handleMobilePhoneLookup}
                      disabled={mobilePhoneLookupLoading || !mobilePhoneInput.trim()}
                      style={{
                        background: mobilePhoneLookupLoading ? '#999' : '#4caf50',
                        minWidth: '150px'
                      }}
                    >
                      {mobilePhoneLookupLoading ? `🔄 ${t('whatsapp.buttons.verifying')}` : `🔍 ${t('whatsapp.buttons.verify_number')}`}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Confirmation step - show found phone details */}
                  <div style={{marginBottom: '20px', padding: '20px', background: '#e3f2fd', borderRadius: '8px', border: '2px solid #2196f3'}}>
                    <h4 style={{margin: '0 0 15px', color: '#1565c0'}}>📋 {t('whatsapp.mobile_modal.confirm_title')}</h4>
                    
                    <div style={{display: 'grid', gap: '10px'}}>
                      <div>
                        <strong style={{color: '#666', fontSize: '12px'}}>{t('whatsapp.mobile_modal.phone_number_label')}</strong>
                        <p style={{margin: '2px 0 0', fontSize: '18px', fontWeight: 'bold', color: '#1976d2'}}>
                          {mobilePhoneLookupResult?.display_phone_number || mobilePhoneInput}
                        </p>
                      </div>
                      
                      {mobilePhoneLookupResult?.verified_name && (
                        <div>
                          <strong style={{color: '#666', fontSize: '12px'}}>{t('whatsapp.mobile_modal.verified_name_label')}</strong>
                          <p style={{margin: '2px 0 0', fontSize: '16px', color: '#333'}}>
                            {mobilePhoneLookupResult.verified_name}
                          </p>
                        </div>
                      )}
                      
                      <div>
                        <strong style={{color: '#666', fontSize: '12px'}}>{t('whatsapp.mobile_modal.waba_id_label')}</strong>
                        <p style={{margin: '2px 0 0', fontSize: '14px', color: '#666', fontFamily: 'monospace'}}>
                          {mobilePhoneLookupResult?.waba_id}
                        </p>
                      </div>
                      
                      <div>
                        <strong style={{color: '#666', fontSize: '12px'}}>{t('whatsapp.mobile_modal.phone_id_label')}</strong>
                        <p style={{margin: '2px 0 0', fontSize: '14px', color: '#666', fontFamily: 'monospace'}}>
                          {mobilePhoneLookupResult?.phone_number_id}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div style={{marginBottom: '15px', padding: '15px', background: '#fff8e1', borderRadius: '8px', border: '1px solid #ffc107'}}>
                    <p style={{margin: 0, fontSize: '14px', color: '#f57f17'}}>
                      <strong>⚠️ {t('whatsapp.mobile_modal.confirm_warning_title')}</strong>
                    </p>
                    <p style={{margin: '8px 0 0', fontSize: '13px', color: '#666'}}>
                      {t('whatsapp.mobile_modal.confirm_warning_desc')}
                    </p>
                  </div>

                  <div style={{marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                    <button 
                      type="button" 
                      className="btn-secondary" 
                      onClick={() => {
                        setShowMobileConfirmation(false);
                        setMobilePhoneLookupResult(null);
                      }}
                    >
                      ← {t('whatsapp.buttons.back')}
                    </button>
                    <button 
                      type="button" 
                      className="btn-primary" 
                      onClick={handleMobileConfirmAndProceed}
                      style={{
                        background: '#4caf50',
                        minWidth: '180px'
                      }}
                    >
                      ✅ {t('whatsapp.buttons.confirm_proceed')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppNumbers;
